import { MAGPIE_CLIENT_LOGGER_NAME, MetricsReporter } from '../config';
import { ConnectionStateManager } from '../connection/connection-status';
import { ConnectionError } from '../errors';
import { Logger } from 'simple-logging-lib';
import { delay, getDeferred, safeCallback } from '../utils';
import { AudienceFilter } from './audience-filter';
export function defineTopic(topicSpec) {
  return {
    spec: topicSpec,
    getName: () => topicSpec.name,
    hasDeliveryConfirmations: () => !!topicSpec.enableDeliveryConfirmation,
    hasRuntimeValidations: () => !!topicSpec.enableRuntimeValidation,
    getAudience: () => topicSpec.audience
  };
}
export class Topic {
  constructor(definition, _proxy, context) {
    this.subscriptionListeners = new Set();
    this.logger = Logger.getLogger(MAGPIE_CLIENT_LOGGER_NAME);
    this.subscribe = async () => {
      const proxy = await this.proxy;
      this.notifySubscriptionChange(true);
      await proxy.topicSubscribe(this.definition.getName());
    };
    this.unsubscribe = async () => {
      const proxy = await this.proxy;
      await proxy.topicUnsubscribe(this.definition.getName());
      this.notifySubscriptionChange(false);
    };
    this.publish = (mBuilder, ...args) => {
      const [options] = args;
      const audienceFilter = this.definition.getAudience();
      mBuilder.setTopic(this.definition.getName()).setCrossPortal(audienceFilter.allowCrossPortal).setAllowCrossTab(audienceFilter.allowCrossTab);
      const errorHandler = error => {
        if (options && options.onError) {
          options.onError(error);
        }
      };
      this.registerReplyCallback(mBuilder, options === null || options === void 0 ? void 0 : options.onReply).catch(errorHandler);
      if (this.definition.hasRuntimeValidations() && options && 'onValidationError' in options) {
        const onValidationError = safeCallback(e => {
          options.onValidationError(e);
          MetricsReporter.reportValidationError(this.context);
        });
        mBuilder.validate(onValidationError);
      }
      this.useProxy().then(proxy => proxy.sendMessage(mBuilder)).then(() => {
        MetricsReporter.reportMessageSent(this.context);
      }).catch(errorHandler);
    };
    this.isMessageTargetAudience = (message, options) => {
      const topicAudience = AudienceFilter.from(this.definition.getAudience());
      const isTargetTopicAudience = topicAudience.isTargetAudience({
        client: this.context,
        message,
        subscriberClientId: options.proxy.clientId,
        subscriberBrokerId: options.proxy.brokerId
      });
      const isTargetSubscriptionAudience = options.filter ? options.filter(message, this.context) : true;
      return isTargetTopicAudience && isTargetSubscriptionAudience;
    };
    this.onSubscriptionChange = listener => {
      this.subscriptionListeners.add(listener);
      return () => {
        this.subscriptionListeners.delete(listener);
      };
    };
    this.removeSubscriptionChangeListener = listener => {
      this.subscriptionListeners.delete(listener);
    };
    this.notifySubscriptionChange = next => {
      this.subscriptionListeners.forEach(listener => {
        try {
          return listener(next);
        } catch (e) {
          this.logger.error(e);
          // Re-throw but make sure other listeners are still called
          setTimeout(() => {
            throw e;
          }, 0);
        }
      });
    };
    this.useProxy = async () => {
      const proxy = await Promise.race([this.proxy, delay(5000).then(() => null)]);
      if (proxy === null) {
        const connectionState = ConnectionStateManager.getInstance();
        throw new ConnectionError(ConnectionError.Reason.UNINITIALIZED, {
          messageArgs: [connectionState.getStatus()]
        });
      }
      return proxy;
    };
    this.registerReplyCallback = (mBuilder, onReplyCallback) => {
      if (!onReplyCallback) {
        return Promise.resolve();
      }
      const replyType = mBuilder.getReplyType();
      if (!replyType) {
        throw new Error('Message does not have a reply type');
      }
      const {
        resolve,
        promise
      } = getDeferred();
      const unsubscribe = this.on(replyType, maybeReplyMessage => {
        if (maybeReplyMessage.type !== replyType || maybeReplyMessage.replyTo !== mBuilder.getId()) {
          return;
        }
        const safeSubscriber = safeCallback(onReplyCallback);
        safeSubscriber(maybeReplyMessage);
        resolve(maybeReplyMessage);
      });
      return promise.then(() => unsubscribe());
    };
    this.definition = definition;
    this.proxy = _proxy;
    this.context = context;
  }
  /**
   * Handles the asynchronous subscription work using the proxy.
   * Obtains a proxy, constructs the message handler and registers it.
   * Adds the resulting unsubscribe function to the provided unsubscribes set.
   */
  async subscribeWithProxy(messageType, safeSubscriber, options, isCancelled) {
    const proxy = await this.useProxy();
    // If cancellation was already requested, skip registering a subscription.
    if (isCancelled()) {
      return () => {
        this.logger.debug('Subscription already cancelled - skipping registration');
      };
    }
    const handleMessage = message => {
      const shouldReject = !this.isMessageTargetAudience(message, Object.assign({}, options, {
        proxy
      }));
      if (shouldReject) {
        return;
      }
      MetricsReporter.reportMessageLatency({
        appName: this.context.appName
      }, Date.now() - message.dispatchedAt);
      this.logger.debug('Received message', message);
      const api = {};
      if (message.replyType) {
        const reply = replyMsg => {
          replyMsg.setReplyTo(message.id);
          this.publish(replyMsg, {
            onError: options === null || options === void 0 ? void 0 : options.onError,
            onConfirmationReceived: undefined,
            onValidationError: undefined,
            onReply: undefined
          });
        };
        Object.assign(api, {
          reply
        });
      }
      void safeSubscriber(message, api);
    };
    return proxy.onMessage(messageType, this.definition.getName(), handleMessage);
  }
  on(messageType, subscriber, options) {
    const unsubscribes = new Set();
    const safeSubscriber = safeCallback(subscriber);
    let cancelled = false;
    const isCancelled = () => cancelled;

    // Execute async subscription work via the helper,
    // passing a cancellation callback.
    this.subscribeWithProxy(messageType, safeSubscriber, options, isCancelled).then(unsubscribe => {
      if (isCancelled()) {
        // If cancelled during async subscription, immediately unsubscribe.
        unsubscribe();
      } else {
        unsubscribes.add(unsubscribe);
      }
    }).catch(error => {
      if (options !== null && options !== void 0 && options.onError) {
        this.logger.error('Failed to subscribe to messages on topic', this.definition.getName(), error);
        options.onError(error);
      }
    });
    return () => {
      cancelled = true;
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }

  /**
   * Subscribes once to a particular message type.
   * Returns a Promise that resolves with the first matching message,
   * which respects the topic's audience and subscription-level filters.
   * If an error occurs during subscription, the Promise is rejected.
   */
  once(messageType, options) {
    const deferred = getDeferred();
    let unsubscribe = null;
    const errorHandler = error => {
      if (unsubscribe) {
        unsubscribe();
      }
      deferred.reject(error);
    };
    try {
      unsubscribe = this.on(messageType, message => {
        if (unsubscribe) {
          unsubscribe();
        }
        deferred.resolve(message);
      }, Object.assign({}, options, {
        onError: errorHandler
      }));
    } catch (error) {
      deferred.reject(error);
    }
    return deferred.promise;
  }
}